/* eslint-disable no-console */
import type Keycloak from "keycloak-js";
import RefreshWorker from "@/functions/workers/keycloakWorker?worker";
import { captureMessageInSentry } from "@/sentry";
import {
  AuthenticationCancelledError,
  refreshAccessTokenInBackground,
} from "./AuthenticationSession";

const RETRY_DELAYS_MS = [5_000, 15_000, 30_000, 60_000] as const;

let worker: Worker | undefined;
let refreshInterval: number | undefined;
let refreshInProgress = false;
let consecutiveFailures = 0;
let nextAttemptAt = 0;
let lifecycleListenersInstalled = false;

async function refresh() {
  if (refreshInProgress || Date.now() < nextAttemptAt) {
    return;
  }

  refreshInProgress = true;
  try {
    await refreshAccessTokenInBackground();
    consecutiveFailures = 0;
    nextAttemptAt = 0;
  } catch (error: unknown) {
    const delay =
      error instanceof AuthenticationCancelledError
        ? 5 * 60_000
        : RETRY_DELAYS_MS[Math.min(consecutiveFailures, RETRY_DELAYS_MS.length - 1)];
    consecutiveFailures += 1;
    nextAttemptAt = Date.now() + delay;
    console.warn(
      `[LISAAuth]: Background token refresh failed; retrying in ${delay / 1000}s.`,
      error,
    );
  } finally {
    refreshInProgress = false;
  }
}

function refreshWhenUsable() {
  if (window.navigator.onLine && document.visibilityState === "visible") {
    nextAttemptAt = 0;
    void refresh();
  }
}

function installLifecycleListeners() {
  if (lifecycleListenersInstalled) {
    return;
  }

  lifecycleListenersInstalled = true;
  window.addEventListener("online", refreshWhenUsable);
  document.addEventListener("visibilitychange", refreshWhenUsable);
}

export async function startRefreshKeycloakAccessTokenInterval(
  _keycloak: Keycloak,
  _forceRestart = false,
) {
  installLifecycleListeners();

  if (window.Worker) {
    console.info("[LISAAuth]: Initializing authentication refresh worker.");
    worker?.terminate();
    worker = new RefreshWorker({ name: "Keycloak_Refresh_Worker" });
    worker.onmessage = (message) => {
      if (message.data.msg === "UPDATE_KEYCLOAK_TOKEN") {
        void refresh();
      }
    };
    worker.postMessage({ msg: "ENABLE_KEYCLOAK_INTERVAL" });
  } else {
    captureMessageInSentry("Authentication refresh worker is unavailable", {
      level: "warning",
    });
    if (refreshInterval != null) {
      window.clearInterval(refreshInterval);
    }
    refreshInterval = window.setInterval(() => void refresh(), 5_000);
  }

  await refresh();
}
